import { useState, useCallback, useRef, useEffect } from 'react';
import { coursesApi } from '../services/coursesApi';
import { isAppleWebkit } from '../../../utils/browser';

/**
 * PDF processing status types
 * @typedef {'QUEUED' | 'EXTRACTING_TEXT' | 'ANALYZING_CONTENT' | 'GENERATING_OUTLINE' | 'CREATING_COURSE' | 'COMPLETED' | 'FAILED'} PdfProcessingStatus
 */

// Maximum time to wait for processing (10 minutes)
const MAX_POLLING_DURATION_MS = 10 * 60 * 1000;
// Maximum consecutive poll failures before showing error
const MAX_CONSECUTIVE_FAILURES = 5;

/**
 * Hook for creating courses from PDF files with real-time status polling.
 *
 * @param {{
 *   onComplete?: (courseId: string, firstLessonId: string | null) => void,
 *   onError?: (error: Error) => void,
 *   pollIntervalMs?: number
 * }} options
 */
export function usePdfCourseCreation(options = {}) {
  const { onComplete, onError, pollIntervalMs = 2000, studentId } = options;

  const [state, setState] = useState({
    isUploading: false,
    isProcessing: false,
    processingId: null,
    status: null,
    progress: 0,
    message: null,
    courseId: null,
    firstLessonId: null,
    error: null,
    estimatedSeconds: null,
  });

  const pollIntervalRef = useRef(null);
  const isMountedRef = useRef(true);
  const pollStartTimeRef = useRef(null);
  const consecutiveFailuresRef = useRef(0);
  const streamAbortRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      try { streamAbortRef.current?.abort(); } catch { /* noop */ }
    };
  }, []);

  // Helper to stop polling and set error
  const stopPollingWithError = useCallback((errorMessage, errorCode = 'POLLING_ERROR') => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    const error = { code: errorCode, message: errorMessage };
    setState(prev => ({
      ...prev,
      isProcessing: false,
      error,
    }));
    const err = new Error(errorMessage);
    err.code = errorCode;
    onError?.(err);
  }, [onError]);

  const pollStatus = useCallback(async (processingId, authEnabled) => {
    // Check for max polling duration timeout
    if (pollStartTimeRef.current && Date.now() - pollStartTimeRef.current > MAX_POLLING_DURATION_MS) {
      stopPollingWithError(
        'Course generation is taking longer than expected. Please check your dashboard later.',
        'TIMEOUT'
      );
      return;
    }

    try {
      const statusResponse = await coursesApi.getPdfProcessingStatus(
        { processingId },
        { auth: authEnabled, studentId }
      );

      if (!isMountedRef.current) return;

      // A malformed/empty status body shouldn't crash the poller — wait for the
      // next tick rather than dereferencing undefined.
      if (!statusResponse) return;

      // Reset consecutive failures on success
      consecutiveFailuresRef.current = 0;

      setState(prev => ({
        ...prev,
        status: statusResponse.status,
        progress: statusResponse.progress,
        message: statusResponse.message,
        courseId: statusResponse.courseId,
        firstLessonId: statusResponse.firstLessonId,
        error: statusResponse.errorCode
          ? { code: statusResponse.errorCode, message: statusResponse.errorMessage || 'Processing failed' }
          : null,
      }));

      // Stop polling on completion or failure
      if (statusResponse.status === 'COMPLETED') {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        // The course exists, but if its first lesson failed to generate we
        // can't drop the learner into a lesson — surface it as a (soft)
        // failure with retry rather than navigating into a broken course.
        if (statusResponse.firstLessonErrorCode) {
          const message = statusResponse.firstLessonErrorMessage
            || 'Your course was created, but the first lesson is still generating. Please try again shortly.';
          setState(prev => ({
            ...prev,
            isProcessing: false,
            error: { code: statusResponse.firstLessonErrorCode, message },
          }));
          const err = new Error(message);
          err.code = statusResponse.firstLessonErrorCode;
          onError?.(err);
        } else {
          setState(prev => ({ ...prev, isProcessing: false }));
          onComplete?.(statusResponse.courseId, statusResponse.firstLessonId);
        }
      } else if (statusResponse.status === 'FAILED') {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setState(prev => ({ ...prev, isProcessing: false }));
        const err = new Error(statusResponse.errorMessage || 'Processing failed');
        err.code = statusResponse.errorCode;
        onError?.(err);
      }
    } catch (err) {
      consecutiveFailuresRef.current += 1;

      if (process.env.NODE_ENV !== 'production') {
        console.warn(`Status poll failed (attempt ${consecutiveFailuresRef.current}):`, err);
      }

      // Show error after too many consecutive failures
      if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
        stopPollingWithError(
          'Unable to check processing status. Please try again or check your dashboard.',
          'CONNECTION_ERROR'
        );
      }
    }
  }, [onComplete, onError, stopPollingWithError, studentId]);

  // Start (or restart) the status poll for a known processingId. Shared by the
  // non-streaming upload and by uploadPdfStream's reconnect fallback — a dropped
  // stream resumes here without re-uploading the file.
  const startPolling = useCallback((processingId, authEnabled) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    pollStartTimeRef.current = Date.now();
    consecutiveFailuresRef.current = 0;
    pollIntervalRef.current = setInterval(
      () => pollStatus(processingId, authEnabled),
      pollIntervalMs
    );
    // Poll immediately so the UI doesn't wait a full interval for the first tick.
    pollStatus(processingId, authEnabled);
  }, [pollStatus, pollIntervalMs]);

  const uploadPdf = useCallback(async (file, uploadOptions = {}, authEnabled = true) => {
    // Reset state
    setState({
      isUploading: true,
      isProcessing: false,
      processingId: null,
      status: null,
      progress: 0,
      message: 'Uploading PDF...',
      courseId: null,
      firstLessonId: null,
      error: null,
      estimatedSeconds: null,
    });

    try {
      const response = await coursesApi.createCourseFromPdf(
        {
          file,
          title: uploadOptions.title,
          difficulty: uploadOptions.difficulty,
          courseDomain: uploadOptions.courseDomain,
          expectedOutcomes: uploadOptions.expectedOutcomes,
        },
        { auth: authEnabled, studentId }
      );

      if (!isMountedRef.current) return;

      setState(prev => ({
        ...prev,
        isUploading: false,
        isProcessing: true,
        processingId: response.processingId,
        status: response.status,
        message: response.message,
        estimatedSeconds: response.estimatedCompletionSeconds,
      }));

      // Start polling for status (immediate first tick + interval)
      startPolling(response.processingId, authEnabled);

    } catch (err) {
      if (!isMountedRef.current) return;

      const error = {
        code: err.code || 'UPLOAD_ERROR',
        message: err.message || 'Failed to upload PDF',
      };

      setState(prev => ({
        ...prev,
        isUploading: false,
        isProcessing: false,
        error,
      }));

      onError?.(err);
    }
  }, [startPolling, onError, studentId]);

  // Stream-first PDF course creation (self-serve). Renders live stage/label/
  // progress from SSE, navigates ONLY on the `complete` event, and — if the
  // stream drops mid-generation — reconnects via status polling on the saved
  // processingId (never re-uploading the file).
  //
  // Two cases skip streaming entirely and use the non-streaming uploadPdf + poll
  // flow instead (still a determinate progress bar, just a ~2s cadence):
  //   - studentId: parent assignment has no stream endpoint.
  //   - Apple/WebKit: Safari + iOS browsers handle a long streamed fetch
  //     unreliably (no body bytes, then a dropped connection) — the poll flow
  //     is rock-solid there. See isAppleWebkit().
  const uploadPdfStream = useCallback(async (file, uploadOptions = {}, authEnabled = true) => {
    if (studentId || isAppleWebkit()) {
      return uploadPdf(file, uploadOptions, authEnabled);
    }

    setState({
      isUploading: true,
      isProcessing: false,
      processingId: null,
      status: null,
      progress: 0,
      message: 'Uploading PDF...',
      courseId: null,
      firstLessonId: null,
      error: null,
      estimatedSeconds: null,
    });

    streamAbortRef.current = new AbortController();
    const { signal } = streamAbortRef.current;

    // Tracked outside state so the catch/after-loop logic can read the latest
    // value synchronously (state updates are async).
    let seenProcessingId = null;
    let terminal = false;

    const surfaceError = (code, message, rawErr) => {
      setState(prev => ({
        ...prev,
        isUploading: false,
        isProcessing: false,
        error: { code, message },
      }));
      onError?.(rawErr ?? Object.assign(new Error(message), { code }));
    };

    try {
      for await (const { event, data } of coursesApi.createCourseFromPdfStream({
        file,
        title: uploadOptions.title,
        difficulty: uploadOptions.difficulty,
        courseDomain: uploadOptions.courseDomain,
        expectedOutcomes: uploadOptions.expectedOutcomes,
        signal,
      })) {
        if (!isMountedRef.current) return;

        if (event === 'stage') {
          if (data.processingId) seenProcessingId = data.processingId;
          setState(prev => ({
            ...prev,
            isUploading: false,
            isProcessing: true,
            processingId: data.processingId ?? prev.processingId,
            status: data.stage ?? prev.status,
            progress: typeof data.completed === 'number' ? data.completed : prev.progress,
            message: data.label ?? prev.message,
          }));
        } else if (event === 'complete') {
          terminal = true;
          setState(prev => ({
            ...prev,
            isUploading: false,
            isProcessing: false,
            progress: 100,
            status: 'COMPLETED',
            courseId: data.courseId,
            firstLessonId: data.firstLessonId,
            error: null,
          }));
          onComplete?.(data.courseId, data.firstLessonId);
          break;
        } else if (event === 'error') {
          terminal = true;
          const message = data.message || 'Course generation failed. Please try again.';
          surfaceError(data.error, message, Object.assign(new Error(message), { code: data.error }));
          break;
        }
      }

      if (!isMountedRef.current) return;

      // Stream ended (reader done) without a terminal frame — the job may still
      // be running server-side. Resume via polling if we have an id to poll.
      if (!terminal) {
        if (seenProcessingId) {
          startPolling(seenProcessingId, authEnabled);
        } else {
          surfaceError('CONNECTION_LOST', 'Connection lost before your course started. Please try again.');
        }
      }
    } catch (err) {
      if (!isMountedRef.current) return;

      if (err?.name === 'AbortError') {
        // User cancel / unmount — reset() owns any state cleanup.
        return;
      }

      // Pre-stream guard failures (bad/oversized/encrypted PDF, auth, rate
      // limit) arrive as a normal 4xx BEFORE any frame and carry a real HTTP
      // status. Treat as a failed request, not a reconnect.
      if (typeof err?.status === 'number') {
        surfaceError(err.code || 'UPLOAD_ERROR', err.message || 'Failed to upload PDF', err);
        return;
      }

      // Transport drop / stall after streaming began. If we saved a
      // processingId, the generation is running independently — reconnect via
      // polling instead of re-uploading. Otherwise surface it.
      if (seenProcessingId) {
        startPolling(seenProcessingId, authEnabled);
      } else {
        surfaceError(err.code || 'CONNECTION_LOST', err.message || 'Connection lost. Please try again.', err);
      }
    }
  }, [studentId, uploadPdf, startPolling, onComplete, onError]);

  const reset = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    try { streamAbortRef.current?.abort(); } catch { /* noop */ }
    streamAbortRef.current = null;
    pollStartTimeRef.current = null;
    consecutiveFailuresRef.current = 0;
    setState({
      isUploading: false,
      isProcessing: false,
      processingId: null,
      status: null,
      progress: 0,
      message: null,
      courseId: null,
      firstLessonId: null,
      error: null,
      estimatedSeconds: null,
    });
  }, []);

  return {
    ...state,
    uploadPdf,
    uploadPdfStream,
    reset,
  };
}
